import { PageHeader } from '@/components/brand/PageHeader';
import { Panel } from '@/components/brand/Panel';
import { SevPill } from '@/components/brand/SevPill';
import { useApiQuery } from '@/lib/hooks';
import { api, ApiError, type SourceHealth } from '@/lib/api';
import { ErrorState, LoadingState, UnauthorizedState } from '@/components/brand/States';

function pill(status: SourceHealth['status']) {
  if (status === 'critical') return <SevPill level="crit" label="critical" />;
  if (status === 'warning') return <SevPill level="warn" label="warning" />;
  if (status === 'no_data') return <SevPill level="info" label="no data yet" />;
  return <SevPill level="ok" label="ok" />;
}

function describeSilence(s: SourceHealth): string {
  if (s.silent_for_sec == null) return 'Never received';
  if (s.silent_for_sec < 60) return `${s.silent_for_sec}s ago`;
  const min = Math.floor(s.silent_for_sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

export function IntegrationsPage() {
  const q = useApiQuery<{ items: SourceHealth[] }>({
    queryKey: ['integrations/health'],
    queryFn: () => api.integrationsHealth(),
    refetchInterval: 30_000,
  });

  return (
    <div>
      <PageHeader eyebrow="Health" title="Integrations" meta="Last event per source" />
      {q.isLoading && <LoadingState />}
      {q.error instanceof ApiError && q.error.isUnauthorized() && <UnauthorizedState />}
      {q.error && !(q.error instanceof ApiError && q.error.isUnauthorized()) && (
        <ErrorState title="Couldn't load integration health" detail={q.error.message} />
      )}
      {q.data && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {q.data.items.map((it) => (
            <Panel key={it.source} title={it.source}>
              {pill(it.status)}
              <p className="text-small text-fg-muted mt-3">
                Expected every {Math.round(it.expected_window_sec / 60)} min
              </p>
              <p className="text-small text-fg-muted mt-1">
                Last event{' '}
                <span className="mono">{describeSilence(it)}</span>
              </p>
              {it.last_event_at && (
                <p className="text-caption text-fg-muted mt-1 mono">{new Date(it.last_event_at).toISOString()}</p>
              )}
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}
